import { inject } from '@angular/core';
import { combineLatest, map, Observable, switchMap, take } from 'rxjs';
import { ActivatedRoute, Params } from '@angular/router';
import { UserService } from '@profile/infra';
import { GetUserProfilePathParams, GetUserProfileResponse } from '@core-http';
import { USER_DATA_TOKEN, UserDataProvider, UserModel } from '@core';
import { UserDetailsQueryResult } from './user-details.query-result';

export class UserDetailsQueryHandler {
  private readonly userService: UserService = inject(UserService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);

  details(): Observable<UserDetailsQueryResult> {
    return this.activatedRoute.params.pipe(
      switchMap((params: Params) => {
        const pathParams: GetUserProfilePathParams = { userId: params['id'] };
        return combineLatest([this.userService.getDetails(pathParams), this.userDataProvider.userData$.pipe(take(1))]);
      }),
      map(([dto, currentUser]: [GetUserProfileResponse, UserModel]) => this.mapToQueryResult(dto, currentUser))
    );
  }

  private mapToQueryResult(dto: GetUserProfileResponse, currentUser: UserModel): UserDetailsQueryResult {
    return {
      userId: dto.userId,
      displayName: dto.displayName,
      email: dto.isMyProfile ? currentUser.email : null,
      thumbnail: dto.thumbnail,
      lastActive: dto.lastActive,
      playerPreferences: {
        preferredPosition: dto.playerPreferences.preferredPosition,
        preferredLevel: dto.playerPreferences.preferredLevel
      },
      userStats: {
        playedGames: dto.userStats.playedGames,
        hostedGames: dto.userStats.hostedGames
      },
      isMyProfile: dto.isMyProfile,
      isVerified: dto.isVerified,
      isHighlighted: dto.isHighlighted,
      rating: {
        hostRating: dto.rating.hostRating,
        hostRatingCount: dto.rating.hostRatingCount
      }
    };
  }
}
